import React, { createContext, useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import jwtDecode from 'jwt-decode';
import PropTypes from 'prop-types';
import { Toast } from 'react-md';
import { toast } from 'react-toastify';
import { axiosTimeout } from '../helpers/axiosTimeout';
import api from '../services/api';
import { useApp } from './Global';

const AuthContext = createContext();
const AuthProvider = ({ children }) => {
  const [data, setData] = useState(() => {
    const token = localStorage.getItem('@Braspress:token');
    const refreshToken = localStorage.getItem('@Braspress:refreshToken');
    const user = localStorage.getItem('@Braspress:user');
    const userAddressList = localStorage.getItem('@Braspress:userAddressList');
    const userContactList = localStorage.getItem('@Braspress:userContactList');

    if (token && user) {
      return {
        token,
        refreshToken,
        user: JSON.parse(user),
        userAddressList: JSON.parse(userAddressList),
        userContactList: JSON.parse(userContactList)
      };
    }

    return {};
  });

  const { searchUser } = useApp();

  const signInSite = useCallback(async ({ cnpj, email, code, password }) => {
    const deviceId = uuid();

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_AUTH}/api/v1/auth/site`,
      {
        document: cnpj,
        email,
        code,
        password,
        deviceId
      },
      {
        timeout: axiosTimeout
      }
    );

    const { legacyToken } = response.data.data;

    return legacyToken;
  }, []);

  const signInInternal = useCallback(async ({ legacyToken }) => {
    if (!legacyToken) return;

    setData({});

    try {
      const jwtDecoded = jwtDecode(legacyToken);

      const { legacyUserId, cnpj, name, email, roles } = jwtDecoded;

      const deviceId = uuid();

      const response = await api.post(
        `${process.env.REACT_APP_URL_MS_AUTH}/api/v1/auth/internal/login/web`,
        {
          token: legacyToken,
          deviceId
        },
        {
          timeout: axiosTimeout
        }
      );

      const { token, refreshToken } = response.data.data;

      const user = {
        name,
        email,
        roles,
        user_id: legacyUserId,
        document: cnpj,
        isReversePickupAllowed: false
      };

      localStorage.setItem('@Braspress:token', token);
      localStorage.setItem('@Braspress:refreshToken', refreshToken);
      localStorage.setItem('@Braspress:legacyToken', legacyToken);
      localStorage.setItem('@Braspress:deviceId', deviceId);

      const searchUserResponse = await searchUser({
        searchData: { document: cnpj },
        token
      });

      const userAddressList = [];
      const userContactList = [];

      if (searchUserResponse?.length > 0) {
        if (searchUserResponse[0]?.isReversePickupAllowed === 'S') {
          user.isReversePickupAllowed = true;
        }

        userAddressList.push({
          additionalInformation: searchUserResponse[0]?.addressComplement || '',
          city: searchUserResponse[0]?.city || '',
          contactName: searchUserResponse[0]?.contact || '',
          contactEmail: searchUserResponse[0]?.email || email || '',
          contactExtension: searchUserResponse[0]?.extension || '',
          contactNumber: searchUserResponse[0]?.telephone || '',
          country: searchUserResponse[0]?.country || '',
          document: searchUserResponse[0]?.document || cnpj || '',
          name: searchUserResponse[0]?.name || name || '',
          state: searchUserResponse[0]?.state || '',
          streetAddress: searchUserResponse[0]?.streetAddress || '',
          streetAddressNumber: searchUserResponse[0]?.streetAddressNumber || '',
          suburb: searchUserResponse[0]?.suburb || '',
          zipPostalCode: searchUserResponse[0]?.zipPostalCode || ''
        });

        userContactList.push({
          email: searchUserResponse[0]?.email || '',
          contactExtension: searchUserResponse[0]?.extension || '',
          contactName: searchUserResponse[0]?.contact || '',
          telephone: searchUserResponse[0]?.telephone || ''
        });

        localStorage.setItem(
          '@Braspress:userAddressList',
          JSON.stringify(userAddressList)
        );
        localStorage.setItem(
          '@Braspress:userContactList',
          JSON.stringify(userContactList)
        );
      } else {
        localStorage.setItem('@Braspress:userAddressList', JSON.stringify([]));
        localStorage.setItem('@Braspress:userContactList', JSON.stringify([]));
      }

      localStorage.setItem('@Braspress:user', JSON.stringify(user));

      setData({ token, refreshToken, user, userAddressList, userContactList });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      return 'Invalid token';
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@Braspress:token');
    localStorage.removeItem('@Braspress:refreshToken');
    localStorage.removeItem('@Braspress:legacyToken');
    localStorage.removeItem('@Braspress:deviceId');
    localStorage.removeItem('@Braspress:user');
    localStorage.removeItem('@Braspress:userAddressList');
    localStorage.removeItem('@Braspress:userContactList');
    setData({});

    window.location.href = `${process.env.REACT_APP_URL_CLIENT_VIEW}${process.env.REACT_APP_URL_BOTAO_VOLTAR}`;
  }, []);

  const verifyToken = useCallback(async (token) => {
    try {
      await api.get(
        `${process.env.REACT_APP_URL_MS_AUTH}/api/v1/auth/authenticated`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: axiosTimeout
        }
      );
    } catch (error) {
      // signOut();
      toast(
        <Toast title="Sessão expirada." content="Faça o login novamente." />,
        { type: 'info' }
      );
    }
  }, []);
  // const userContactList = [
  //   {
  //     email: data.userAddressList?.email || '',
  //     contactExtension: data.userAddressList?.contactExtension || '',
  //     userAddressList: data.userAddressList?.contact || '',
  //     telephone: data.userAddressList?.telephone || ''
  //   }
  // ];

  const contextValues = {
    user: data.user,
    userAddressList: data.userAddressList,
    userContactList: data.userContactList,
    signInSite,
    signInInternal,
    signOut,
    verifyToken,
    token: data.token,
    refreshToken: data.refreshToken
  };

  return (
    <AuthContext.Provider value={contextValues}>
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

AuthProvider.propTypes = {
  children: PropTypes.node
};

export { AuthProvider, useAuth };
